import React, { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { Modal, Button, Form, Row, Col } from "react-bootstrap";
import apiClient from "../api/apiClient";
import { useIndexPageData } from "../contextapi/Indexpagedata";
import { toast } from "react-toastify";

const stripePromise = loadStripe(
  "pk_test_51PFBb8RqldiTE8bjsee3CEN27FqrzLCtmRrJoCpzS9dZ8kiU2JMJZzJLManEn3ctsBRZ3xQ00Q9XLPMXbsDKt05b00fbfyRqK1"
);

const cardElementOptions = {
  style: {
    base: {
      fontSize: "16px",
      color: "#32325d",
      fontFamily: "Arial, sans-serif",
      "::placeholder": { color: "#a0aec0" },
    },
    invalid: { color: "#e53e3e" },
  },
};

function SubscriptionForm({ onClose, onSubscribed }) {
  const stripe = useStripe();
  const elements = useElements();
  const { profiledata } = useIndexPageData();
  const [cardholderName, setCardholderName] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setErrorMessage(null);

    try {
      // 1️⃣ Create PaymentMethod
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: "card",
        card: elements.getElement(CardNumberElement),
        billing_details: {
          name: cardholderName,
          email: profiledata?.email,
        },
      });

      if (error) {
        setErrorMessage(error.message);
        setLoading(false);
        return;
      }

      // 2️⃣ Send to backend
      const res = await apiClient.post("/subscription/", {
        payment_method_id: paymentMethod.id,
      });

      const subscription = res?.data?.subscription;

      if (
        subscription?.latest_invoice?.payment_intent?.status ===
        "requires_action"
      ) {
        const clientSecret =
          subscription.latest_invoice.payment_intent.client_secret;
        const { error: confirmError } = await stripe.confirmCardPayment(
          clientSecret
        );

        if (confirmError) {
          setErrorMessage(confirmError.message);
          setLoading(false);
          return;
        }
      }

      toast.success("Successfully subscribed for seller pro!");
      onSubscribed();
      onClose();
    } catch (err) {
      console.error(err);
      toast.error("✅ Something went wrong while purchasing subscription!");
      setErrorMessage("Something went wrong.");
    }

    setLoading(false);
  };

  return (
    <form onSubmit={handleSubscribe}>
      <Form.Group className="mb-3">
        <Form.Label>Cardholder name</Form.Label>
        <Form.Control
          type="text"
          placeholder="Full name on card"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          required
        />
      </Form.Group>

      {/* Card Info */}
      <Form.Group className="mb-3">
        <Form.Label>Card information</Form.Label>
        <div className="border rounded">
          <div className="p-3 border-bottom d-flex align-items-center justify-between">
            <CardNumberElement
              options={cardElementOptions}
              className="flex-grow-1"
            />

            <div className="d-flex ms-3 gap-2" style={{ width: "120px" }}>
              <img
                src="../assets/images/visa.svg"
                alt="Visa"
                style={{ width: "30px" }}
              />
              <img
                src="../assets/images/mastercard.png"
                alt="MasterCard"
                style={{ width: "30px" }}
              />
              <img
                src="../assets/images/unionpay.png"
                alt="UnionPay"
                style={{ width: "30px" }}
              />
            </div>
          </div>

          <Row className="g-0">
            <Col xs={6}>
              <div className="p-3 border-end">
                <CardExpiryElement options={cardElementOptions} />
              </div>
            </Col>
            <Col xs={6}>
              <div className="p-3">
                <CardCvcElement options={cardElementOptions} />
              </div>
            </Col>
          </Row>
        </div>
      </Form.Group>

      {errorMessage && (
        <div className="alert alert-danger small">{errorMessage}</div>
      )}

      <Button
        type="submit"
        className="w-100"
        disabled={!stripe || loading}
        style={{ backgroundColor: "#c93312", border: "none" }}
      >
        {loading ? "Processing..." : "Subscribe"}
      </Button>
      <div className="text-center mt-3 small text-muted">
        Powered by <strong>Stripe</strong> |{" "}
        <a
          href="https://stripe.com/legal/consumer"
          target="_blank"
          rel="noopener noreferrer"
          className="text-decoration-none"
        >
          Terms
        </a>{" "}
        |{" "}
        <a
          href="https://stripe.com/privacy"
          target="_blank"
          rel="noopener noreferrer"
          className="text-decoration-none"
        >
          Privacy
        </a>
      </div>
    </form>
  );
}

export default function SubscriptionModal({ open, onClose, onSubscribed }) {
  return (
    <Elements stripe={stripePromise}>
      <Modal show={open} onHide={onClose} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fw-bold text-center w-100" >Subscribe to Seller Pro</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <SubscriptionForm onClose={onClose} onSubscribed={onSubscribed} />
        </Modal.Body>
      </Modal>
    </Elements>
  );
}
